import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardList, AlertTriangle, ArrowRight } from "lucide-react";
import api from "../../lib/api";

const REVIEW_QUEUE_ROLES = new Set([
  "leadership",
  "read_only_leadership",
  "compliance",
  "legal_review",
]);

function severityClass(sev) {
  if (sev === "red") {
    return "bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/60";
  }
  if (sev === "amber") {
    return "bg-amber-100 text-amber-700 border border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/60";
  }
  return "bg-[color:var(--surface-2)] text-[color:var(--page-fg)] border border-slate-300 dark:bg-slate-700/40 dark:text-[color:var(--page-fg-secondary)] dark:border-slate-600";
}

export default function MyReviewQueueCard({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const role = user?.role;
  const visible = REVIEW_QUEUE_ROLES.has(role);

  useEffect(() => {
    if (!visible) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get("/dashboard/my-review-queue?limit=5")
      .then((r) => {
        if (!cancelled) {
          setData(r.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.status === 403 ? null : err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (!visible || error || (!loading && !data)) return null;

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const aggregated = data?.aggregated;
  const slBreakdown = data?.by_service_line || {};

  return (
    <section
      data-testid="my-review-queue-card"
      className="brand-card mt-6 p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
            style={{
              backgroundColor: "rgba(20,184,184,0.16)",
              color: "var(--page-fg)",
            }}
          >
            <ClipboardList className="h-4 w-4" />
          </span>
          <div>
            <h2 className="font-serif text-base font-semibold text-[color:var(--page-fg)] dark:text-[color:var(--page-fg)]">
              My Review Queue ·{" "}
              <span data-testid="review-queue-total" className="text-brand-teal dark:text-emerald-400">
                {total}
              </span>{" "}
              awaiting review
            </h2>
            <p className="mt-0.5 text-xs text-[color:var(--page-fg-muted)]">
              {aggregated
                ? "Aggregate counts only for read-only leadership."
                : "Pending Review cases sorted by oldest first. Amber after 7 days, red after 14."}
            </p>
          </div>
        </div>
        <Link
          to="/cases?statuses=Pending+Review"
          data-testid="review-queue-view-all"
          className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium text-brand-teal hover:bg-[color:var(--surface-2)] dark:text-emerald-300 dark:hover:bg-emerald-900/20"
        >
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {loading && (
        <p className="mt-4 text-sm text-[color:var(--page-fg-muted)]">Loading review queue…</p>
      )}

      {!loading && total === 0 && (
        <p className="mt-4 text-sm text-[color:var(--page-fg-muted)]" data-testid="review-queue-empty">
          No cases pending review right now.
        </p>
      )}

      {!loading && total > 0 && aggregated && (
        <div data-testid="review-queue-aggregate" className="mt-4 flex flex-wrap gap-2">
          {Object.entries(slBreakdown).map(([sl, count]) => (
            <span
              key={sl}
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--surface-2)] px-3 py-1 text-xs font-medium text-[color:var(--page-fg-secondary)] dark:bg-white/5 dark:text-[color:var(--page-fg-secondary)]"
            >
              <span className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                {sl}
              </span>
              <span className="font-semibold">{count}</span>
            </span>
          ))}
        </div>
      )}

      {!loading && total > 0 && !aggregated && (
        <ul className="mt-4 space-y-2">
          {items.map((c) => (
            <li
              key={c.id}
              data-testid={`review-queue-row-${c.id}`}
              className="flex flex-wrap items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] px-3 py-2 transition hover:bg-[color:var(--surface-2)] dark:border-white/10 dark:hover:bg-white/5"
            >
              <button
                type="button"
                className="font-mono text-xs font-semibold text-[color:var(--page-fg)] hover:underline dark:text-[color:var(--page-fg)]"
                onClick={() => navigate(`/cases/${c.id}`)}
              >
                {c.case_id}
              </button>
              <span className="text-xs text-[color:var(--page-fg-muted)]">·</span>
              <span className="text-xs text-[color:var(--page-fg-secondary)] dark:text-[color:var(--page-fg-secondary)]">
                {c.team_member_name || `Position ${c.team_member_position_id || c.team_member_file_number}`}
              </span>
              {c.service_line && (
                <span className="rounded-full bg-[color:var(--surface-2)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[color:var(--page-fg-muted)] dark:bg-white/5 dark:text-[color:var(--page-fg-secondary)]">
                  {c.service_line}
                </span>
              )}
              <span className="text-xs text-[color:var(--page-fg-secondary)] dark:text-[color:var(--page-fg-secondary)]">
                {c.issue_type}
              </span>
              {c.high_risk && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:bg-red-900/30 dark:text-red-200">
                  <AlertTriangle className="h-3 w-3" />
                  High-risk
                </span>
              )}
              <span className="ml-auto inline-flex items-center gap-1">
                <span
                  className={
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                    severityClass(c.severity)
                  }
                  data-testid={`review-queue-days-${c.id}`}
                  title={c.entered_pending_review_at}
                >
                  {c.days_in_pending_review}d
                </span>
                {c.assigned_hr_partner_name && (
                  <span
                    className="hidden text-[11px] text-[color:var(--page-fg-muted)] sm:inline"
                    title={`Assigned: ${c.assigned_hr_partner_name}`}
                  >
                    · {c.assigned_hr_partner_name}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
